import { useLocation } from 'react-router-dom';
import { AnonymousEntry } from './platform/productEntry/surface';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { ArrowRight, CheckCircle2, KeyRound, MessageSquareText, ShieldCheck, Sparkles } from 'lucide-react';
import { useAuth } from './auth/AuthContext';
import { BrandMark } from './components/BrandMark';
import { Spinner } from './components/ui';
import { PublicLegalLinks } from './components/PublicLegalLinks';
import { authApi } from './api/auth';
import WebPlatformApp from './platform/WebPlatformApp';

export default function App() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="oa-auth-loading" role="status">
        <div className="oa-loading-mark"><Sparkles /></div>
        <Spinner label="Открываем рабочее пространство…" />
      </div>
    );
  }

  if (!user) return <LoginPage />;

  // TMA-origin pages read a Telegram-shaped profile for greeting/avatar only.
  // Authentication itself always stays in the secure web cookie/Bearer layer.
  (window as any).__OA_WEB_USER__ = user;
  return <WebPlatformApp />;
}

function LoginPage() {
  const location=useLocation();
  const { onLoggedIn } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [phase, setPhase] = useState<'idle' | 'waiting'>('idle');
  const [code, setCode] = useState('');
  const [submittingCode, setSubmittingCode] = useState(false);
  const pollRef = useRef<number | null>(null);
  const botUsername = import.meta.env.VITE_TELEGRAM_BOT_USERNAME ?? '';

  const stopPolling = () => {
    if (pollRef.current !== null) {
      window.clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  useEffect(() => () => stopPolling(), []);

  const handleTelegramLogin = async () => {
    if (!botUsername) {
      setError('Не настроено имя Telegram-бота. Попробуйте вход по коду.');
      return;
    }
    setError(null);

    // Открываем вкладку синхронно с кликом. После await браузер уже может
    // заблокировать window.open, а основную вкладку мы не перенаправляем никогда.
    const telegramTab = window.open('about:blank', '_blank');
    if (!telegramTab) {
      setError('Браузер заблокировал новую вкладку. Разрешите всплывающие окна и попробуйте ещё раз.');
      return;
    }
    try {
      const { token } = await authApi.startLogin();
      const deepLink = `https://t.me/${botUsername}?start=apw_${token}`;
      telegramTab.location.replace(deepLink);
      setPhase('waiting');
      stopPolling();
      pollRef.current = window.setInterval(async () => {
        try {
          const profile = await authApi.pollLogin(token);
          if (profile) {
            stopPolling();
            onLoggedIn(profile);
          }
        } catch {
          stopPolling();
          setPhase('idle');
          setError('Ссылка для входа истекла. Нажмите «Открыть Telegram» ещё раз.');
        }
      }, 2000);
    } catch (e) {
      if (!telegramTab.closed) telegramTab.close();
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const handleCodeLogin = async (event: FormEvent) => {
    event.preventDefault();
    if (code.trim().length !== 6) return;
    setSubmittingCode(true);
    setError(null);
    try {
      const profile = await authApi.loginWithCode(code.trim());
      onLoggedIn(profile);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmittingCode(false);
    }
  };

  const handleDevLogin = async () => {
    setError(null);
    try { onLoggedIn(await authApi.devLogin()); }
    catch (e) { setError(e instanceof Error ? e.message : String(e)); }
  };

  return (
    <main className="oa-login">
      <div className="oa-login-ambient" aria-hidden="true" />
      <section className="oa-login-intro">
        <div className="oa-login-brand"><span><BrandMark /></span>Очередной агент</div>
        <AnonymousEntry search={location.search} onLogin={()=>{document.querySelector('.oa-login-panel')?.scrollIntoView({behavior:'smooth',block:'start'});document.getElementById('telegram-code')?.focus();}}/>
        <div className="oa-login-copy">
          <p className="oa-eyebrow">ИИ-команда в одном окне</p>
          <h1>Поручайте работу.<br />Не переключайтесь между сервисами.</h1>
          <p>Ассистент, автоматизации, агенты, файлы и подключения теперь доступны в полноценном интерфейсе для компьютера.</p>
        </div>
        <div className="oa-login-features" aria-label="Возможности платформы">
          <div><MessageSquareText /><span><strong>Чат с оркестратором</strong><small>Файлы, голос, потоковые ответы и история</small></span></div>
          <div><Sparkles /><span><strong>Автоматизации без кода</strong><small>Опишите результат — система соберёт сценарий</small></span></div>
          <div><ShieldCheck /><span><strong>Ваши данные под контролем</strong><small>Настройки памяти, связей и аккаунта в одном месте</small></span></div>
        </div>
      </section>

      <section className="oa-login-panel" aria-labelledby="login-title">
        <div className="oa-login-card">
          <div className="oa-login-card-icon"><KeyRound /></div>
          <p className="oa-eyebrow">Без нового пароля</p>
          <h2 id="login-title">Войти через Telegram</h2>
          <p className="oa-login-hint">Telegram подтвердит вашу личность, а рабочая сессия останется открыта в этом браузере.</p>

          <button className="oa-login-primary" onClick={handleTelegramLogin} disabled={phase === 'waiting'}>
            {phase === 'waiting' ? 'Ждём подтверждение…' : 'Открыть Telegram'}
            {phase === 'waiting' ? <span className="oa-button-loader" /> : <ArrowRight />}
          </button>

          {phase === 'waiting' && (
            <div className="oa-login-wait" role="status">
              <CheckCircle2 />
              <span>В боте нажмите <b>Start / Запустить</b>, затем вернитесь в эту вкладку. Вход завершится автоматически.</span>
            </div>
          )}

          <div className="oa-login-divider"><span>или войдите по коду</span></div>

          <form onSubmit={handleCodeLogin} className="oa-code-form">
            <label htmlFor="telegram-code">Одноразовый код</label>
            <p>Отправьте боту команду <code>/login</code> и введите шесть цифр.</p>
            <div>
              <input
                id="telegram-code"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                placeholder="000 000"
                value={code}
                onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
              />
              <button type="submit" disabled={submittingCode || code.length !== 6}>
                {submittingCode ? 'Проверяем…' : 'Войти'}
              </button>
            </div>
          </form>

          {error && <div className="oa-login-error" role="alert">{error}</div>}

          <PublicLegalLinks />

          {import.meta.env.DEV && (
            <button className="oa-dev-login" onClick={handleDevLogin}>Dev-вход</button>
          )}
        </div>
      </section>
    </main>
  );
}
